import * as React from "react";
import { useEffect, useRef, useState } from "react";
import { useLocation } from "react-router-dom";
import { Helmet } from "react-helmet-async";
import { GAInitialize } from "../../GoogleAnalytics";
import { useSEO } from "../../../context/SEOContext";


interface Props {
  content?: any;
}


/**
 * Build canonical using origin + last path segment.
 * Rules:
 * - Absolute URL.
 * - Uses pathname only (no query/hash).
 * - Removes leading "preview/" if present.
 * - Normalizes multiple slashes and trailing slash.
 * - Root path => origin (or origin/home if you prefer).
 * - Optionally enforce lowercase and trailing slash policy.
 */
function buildCanonicalFromLocation(
  origin: string,
  pathname: string,
  options?: {
    enforceLowercase?: boolean;
    enforceTrailingSlash?: boolean; // false = no trailing slash
    homepageSlug?: string | null;   // e.g., "home" if you want origin/home
  }
): string {
  const {
    enforceLowercase = true,
    enforceTrailingSlash = false,
    homepageSlug = null, // null => origin (no /home)
  } = options || {};

  // 1) Sanitize duplicate slashes (keep protocol intact)
  let clean = pathname.replace(/\/{2,}/g, "/");

  // 2) Remove "preview/" only at the start
  clean = clean.replace(/^\/?preview\//, "/");

  // 3) If root, return origin or origin/home
  if (!clean || clean === "/") {
    if (homepageSlug && homepageSlug.trim()) {
      const home = `${origin}/${homepageSlug.trim()}`;
      return enforceTrailingSlash ? `${home}/` : home;
    }
    return enforceTrailingSlash ? `${origin}/` : origin;
  }

  // 4) Remove trailing slash
  if (clean.length > 1 && clean.endsWith("/")) {
    clean = clean.slice(0, -1);
  }

  // 5) Extract last segment
  const segments = clean.split("/").filter(Boolean);
  let slug = segments[segments.length - 1] || "";

  // 6) Optional lowercase canonical slugs
  if (enforceLowercase) {
    slug = slug.toLowerCase();
  }

  // 7) Basic validation/normalization
  if (slug.includes("//")) {
    slug = "path-not-correct";
  }

  // 8) Compose canonical
  const canonical = `${origin}/${slug}`;
  return enforceTrailingSlash ? `${canonical}/` : canonical;
}

/**
 * SEOMeta Component
 * @param content - Content from the CMS.
 */
const SEOMeta = ({ content }: Props) => {
  // const meta = useRef(null);
  // const [load, setLoad] = useState(false);

  const meta = useRef<string | null>(null);
  const [load, setLoad] = useState(false);

  const location = useLocation();
  const baseUrl = window.location.origin; // consider normalizing to https if needed
  const pathname = location.pathname || "/";

  useEffect(() => {
    if (meta.current === null) {
      meta.current = "loaded";
      setLoad(true);
    }
  }, []);

  const { seo } = useSEO();
// Priority:
  // 1) Context canonical (e.g., events set via SEOContext)
  // 2) CMS canonical (Contentful)
  // 3) Fallback: origin + last segment of current path
  const canonicalUrl =
    seo?.canonicalUrl ||
    content?.fields?.seo?.fields?.canonicalUrl ||
    buildCanonicalFromLocation(baseUrl, pathname, {
      enforceLowercase: true,
      enforceTrailingSlash: false,
      homepageSlug: null, // set to "home" if you want origin/home
    });

  const title =
    seo?.title ||
    content?.fields?.pageTitle ||
    "Welcome to Emergency Services and State Super - ESSSuper";

  const description =
    content?.fields?.seo?.fields?.seodescription ||
    seo?.description ||
    "";

  const hide = !!content?.fields?.seo?.fields?.hidePageFromSearchEngine;
  const exclude = !!content?.fields?.seo?.fields?.excludeLinksFromSearchEngineRanking;

 
  return (
    <>
      {load && (
        <>
          <Helmet>
            <title>{title}</title>

            {/* Meta Description */}
            <meta name="description" content={description} />

            {/* Canonical */}
            {canonicalUrl && <link rel="canonical" href={canonicalUrl} />}

            {/* Open Graph */}
            <meta property="og:type" content="website" />
            {content?.fields?.seo?.fields?.seoTitle && (
              <meta property="og:title" content={content?.fields?.seo?.fields?.seoTitle} />
            )}
            {content?.fields?.seo?.fields?.seodescription && (
              <meta property="og:description" content={content?.fields?.seo?.fields?.seodescription} />
            )}
            {content?.fields?.seo?.fields?.featuredimage && (
              <meta
                property="og:image"
                content={content?.fields?.seo?.fields?.featuredimage?.fields?.file?.url}
              />
            )}

            {/* Robots meta logic */}
            {seo?.noIndex ? (
              <meta name="robots" content="noindex, nofollow" />
            ) : hide ? (
              <meta
                name="robots"
                content={`${hide ? "noindex" : "index"}, ${exclude ? "nofollow" : "follow"}`}
              />
            ) : (
              <meta name="robots" content="index, follow" />
            )}
          </Helmet>

          <GAInitialize data-testid="gaInitialize" />
        </>
      )}
    </>
  );
};

export default SEOMeta;
